import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../components/ui/Button'
import { ErrorNote } from '../components/ui/ErrorNote'
import {
  useVehicleDraft,
  VEHICLE_STEP_HINTS,
  VEHICLE_STEP_TITLES,
  type VehicleStepId,
} from '../components/vehicle/useVehicleDraft'
import { WizardShell } from '../components/wizard/WizardShell'
import { ApiError } from '../lib/api'
import { saveVehicle } from '../lib/vehicles-api'

/**
 * «افزودن خودرو» — registering a car on its own, with no quote attached. The same steps the
 * motor wizard runs for an unsaved car; once saved, quoting it is two taps from the garage.
 */
export function VehicleFormPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const vehicle = useVehicleDraft()
  const [stepId, setStepId] = useState<VehicleStepId>('model')

  const index = Math.max(0, vehicle.steps.indexOf(stepId))
  const step = vehicle.steps[index] as VehicleStepId
  const isLast = index === vehicle.steps.length - 1

  const save = useMutation({
    mutationFn: () => saveVehicle(vehicle.body()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      navigate('/profile/vehicles', { replace: true })
    },
  })

  const error = save.error instanceof ApiError ? save.error : undefined
  const messages = error ? (error.fields ? Object.values(error.fields) : [error.messageFa]) : []

  return (
    <WizardShell
      title={VEHICLE_STEP_TITLES[step]}
      hint={VEHICLE_STEP_HINTS[step]}
      step={index + 1}
      totalSteps={vehicle.steps.length}
      onBack={() =>
        index === 0
          ? navigate('/profile/vehicles')
          : setStepId(vehicle.steps[index - 1] as VehicleStepId)
      }
      footer={
        <>
          <Button
            disabled={!vehicle.complete[step] || save.isPending}
            loading={save.isPending}
            onClick={() =>
              isLast ? save.mutate() : setStepId(vehicle.steps[index + 1] as VehicleStepId)
            }
          >
            {isLast ? 'ذخیره خودرو' : 'ادامه'}
          </Button>
          {messages.map((message) => (
            <ErrorNote key={message}>{message}</ErrorNote>
          ))}
        </>
      }
    >
      {vehicle.render(step)}
    </WizardShell>
  )
}
